/**
 * MobileModeSwitcher — bottom-pinned icon-only viewer mode dock.
 *
 * Two-way for Le Vanilla (about ↔ cameras), three-way for Le Vanilla + Map
 * (about ↔ cameras ↔ map). Modes are driven by config so the same primitive
 * serves both shells.
 *
 * Sets `data-lv-mode` on document.body — the shell CSS uses this attribute
 * to show one purposeful overlay at a time at every viewport size.
 *
 * The modes are toggle buttons (aria-pressed) in a labelled group: they swap overlays rather than own tab panels. When
 * the shell's surface turns to a camera from elsewhere (the rail or a map sphere), the dock follows, so it never says
 * Route map while a camera fills the canvas.
 */

import { useEffect, useState } from 'react'
import { Info, MapPin, Video, type LucideIcon } from 'lucide-react'
import {
  registerPrimitive,
  useShellSurface,
  type PrimitiveProps,
} from '../../layout'

export type MobileModeKey = 'about' | 'cameras' | 'map'

interface MobileModeSwitcherConfig {
  /** Which modes to expose. Default ["about", "cameras"]. */
  modes?: MobileModeKey[]
  /** Mode active on first render. Default the first entry of `modes`. */
  defaultMode?: MobileModeKey
}

const ICONS: Record<MobileModeKey, LucideIcon> = {
  about: Info,
  cameras: Video,
  map: MapPin,
}

const LABELS: Record<MobileModeKey, string> = {
  about: 'About this event',
  cameras: 'Cameras',
  map: 'Route map',
}

const RESTORE_LABELS: Record<Exclude<MobileModeKey, 'map'>, string> = {
  about: 'Show event details',
  cameras: 'Show camera list',
}

export function ViewSwitcher({ config }: PrimitiveProps) {
  const { mode: surfaceMode, requestMode } = useShellSurface()
  const cfg = config as MobileModeSwitcherConfig
  const modes: MobileModeKey[] =
    cfg.modes && cfg.modes.length > 0 ? cfg.modes : ['about', 'cameras']
  const [mode, setMode] = useState<MobileModeKey>(cfg.defaultMode ?? modes[0])
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    if (surfaceMode === 'camera' && mode === 'map' && modes.includes('cameras')) setMode('cameras')
  }, [surfaceMode, mode, modes])

  // Reflect onto document.body so the shell's CSS attribute selectors fire.
  useEffect(() => {
    document.body.dataset.lvMode = collapsed ? 'hidden' : mode
    return () => {
      delete document.body.dataset.lvMode
    }
  }, [collapsed, mode])

  return (
    <div className="lv-mobile-switcher" role="group" aria-label="View mode">
      {modes.map((m) => {
        const Icon = ICONS[m]
        const isActive = m === mode && !collapsed
        const label = m === mode && collapsed && m !== 'map'
          ? RESTORE_LABELS[m]
          : LABELS[m]
        return (
          <button
            key={m}
            type="button"
            aria-pressed={isActive}
            aria-label={label}
            title={label}
            className={`lv-mms-btn${isActive ? ' lv-mms-btn-active' : ''}`}
            onClick={() => {
              if (m === mode && m !== 'map') {
                setCollapsed((current) => !current)
                return
              }
              setMode(m)
              setCollapsed(false)
              if (m === 'map') requestMode('map')
              if (m === 'cameras') requestMode('camera')
            }}
          >
            <Icon size={18} aria-hidden="true" />
          </button>
        )
      })}
    </div>
  )
}

registerPrimitive('ViewSwitcher', ViewSwitcher)
registerPrimitive('MobileModeSwitcher', ViewSwitcher)

export { ViewSwitcher as MobileModeSwitcher }
